import React from 'react';
import { useTranslation } from 'react-i18next';

import { connectPage, IPageProps } from '@lialo/core/lib/pages/tourDetail/connect';
import routes from '@lialo/core/lib/pages/utils/routes';
import { LoadingScreen } from '@lialo/pwa-ui/lib/PageComponents/Loading';
import { errorTranslationKey } from '@lialo/core/lib/translations/translationHelper';
import { Redirect } from 'react-router-dom';
import { AsyncError, ErrorType } from '@lialo/core/lib/common/AsyncError';
import ErrorPage from '../../common/Page/ErrorPage/ErrorPage';
import { useEffectWithStatus } from '@lialo/core/lib/utils/hooks';
import { useServiceProvider } from '../../common/Providers';
import TourDetail from './TourDetail/TourDetail';
import TourCheckoutPage from './Checkout/TourCheckoutPage';
import { Route, Switch } from 'react-router';
import NotFoundPage from '../Other/NotFoundPage';
import { PurchaseCouponPage } from './PurchaseCoupon';

const TourDetailPage = ({
  tourCode,
  tour,
  isLoading,
  error,
  fetchTourDetails,
  tourRatings,
  returnToLocation,
  numberOfComments,
  requiresPurchase,
}: IPageProps) => {
  const { analyticsService, navigationService } = useServiceProvider();
  const { t } = useTranslation();

  const prepareTour = React.useCallback(() => {
    fetchTourDetails(tourCode);
  }, [fetchTourDetails, tourCode]);

  const isEffectCalled = useEffectWithStatus(() => {
    prepareTour();
  }, [prepareTour]);

  React.useEffect(() => {
    if (tour && tour.startLocation) {
      analyticsService.logShowTourDetail(tour.shortLink, tour.user.id, tour.startLocation.city, tour.price);
    }
  }, [analyticsService, tour]);

  if (isLoading || !isEffectCalled) {
    return <LoadingScreen />;
  }

  if (error) {
    if (error instanceof AsyncError && error.errorCode === ErrorType.TOUR_NOT_FOUND) {
      return (
        <Redirect
          to={routes.createLocationDescriptorObject(navigationService.generateI18nPath(routes.ROOT), {
            messageKey: errorTranslationKey(error),
          })}
        />
      );
    } else {
      return (
        <ErrorPage
          parentUrl={navigationService.generateI18nPath(routes.ROOT)}
          message={t(errorTranslationKey(error))}
          retry={prepareTour}
        />
      );
    }
  }

  if (tour) {
    return (
      <Switch>
        {!tour.purchased && <Route path={routes.TOUR_CHECKOUT} exact={true} component={TourCheckoutPage} />}
        {<Route path={routes.TOUR_PURCHASE_COUPON} exact={true} component={PurchaseCouponPage} />}
        <Route path={[routes.TOUR_DETAIL, routes.TOUR_INSIGHT]} exact={true}>
          <TourDetail
            tourCode={tourCode}
            tour={tour}
            tourRatings={tourRatings}
            returnToLocation={returnToLocation}
            numberOfComments={numberOfComments ?? 0}
            requiresPurchase={requiresPurchase}
          />
        </Route>
        <NotFoundPage />
      </Switch>
    );
  }
  return null;
};

export default connectPage(TourDetailPage);
